import { Button } from "@deliverr/ui/lib-esm/Button";
import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { defaultTheme as theme } from "@deliverr/ui/lib-esm/shared/theme";
import styled from "@emotion/styled";

import {
  maxASNLength,
  maxWarehouseLength,
  DSKULength,
} from "../../common/utils/config";
import {
  OpsInputForm,
  OpsInputFormInput,
  OpsInputFormInputUpper,
  OpsInputFormInputLower,
  OpsInputFormInputSection,
  OpsInputFormSubTitle,
  OpsInputFormSelect,
  OpsInputFormTextArea,
} from "../../common/emotion/forms";
import { TableComponent } from "../../common/components/TableComponent";
import { TableInputGap } from "../../common/components/TableInputGap";
import { LostUnitsField } from "./LostUnitsForm.types";
import { useLostUnitsForm } from "./useLostItemsForm";
import { inventoryClient } from "../LostUnitsList/LostUnitsList";
import { LoadingSpinner } from "../../common/components/LoadingSpinner";
import { CenteredBlock } from "../../common/components/CenteredBlock";
import moment from "moment";

const FormErrorMessage = styled.td`
  background-color: ${theme.colors.RED[400]};
  color: black;
  font-family: ${theme.font.family.STANDARD};
  font-size: ${theme.font.size.F2};
  font-weight: ${theme.font.weight.BOLD};
  text-align: center;
  padding: ${theme.spacing.S3};
`;

const FormSuccessMessage = styled.td`
  color: ${theme.colors.GREEN[500]};
  font-family: ${theme.font.family.STANDARD};
  font-size: ${theme.font.size.F2};
  text-align: center;
  padding: ${theme.spacing.S3};
`;

const MissingRecordMessage = styled.span`
  color: ${theme.colors.RED[500]};
  font-family: ${theme.font.family.STANDARD};
  font-size: ${theme.font.size.F2};
  font-weight: ${theme.font.weight.BOLD};
  padding: ${theme.spacing.S3};
`;

const isSuccess = (message: string): boolean => {
  return message.substring(0, 7) === "Success";
};

const onDelete = (id: number) => {
  try {
    if (window.confirm("Are you sure you want to delete this?")) {
      inventoryClient.deleteLostUnits(id);
      window.open("/LostUnits/List/", "_self");
    }
  } catch (e) {
    window.alert(e);
  }
};

export interface LostUnitsFormProps {
  recordId?: number;
}

export const LostUnitsForm: React.FC<LostUnitsFormProps> = ({ recordId }) => {
  const { errors, formState, formError, onFormSubmit, refs, formHandler } =
    useLostUnitsForm();

  const { reset, trigger } = formHandler;

  const [data, setData] = useState({
    record: undefined as any,
    recordMissing: false,
  });

  useEffect(() => {
    if (recordId !== undefined) {
      inventoryClient.getLostUnit(recordId).then((data) => {
        if (data?.id === undefined) {
          setData({ record: undefined, recordMissing: true });
        } else {
          const fixedData = {
            ...data,
            lostAt:
              data?.lostAt !== undefined
                ? moment(data.lostAt).format("YYYY-MM-DD")
                : undefined,
          };
          setData({ record: fixedData, recordMissing: false });
          reset(fixedData);
          trigger();
        }
      });
    }
  }, []);

  if (recordId !== undefined) {
    if (data?.recordMissing === true) {
      return (
        <CenteredBlock>
          <MissingRecordMessage>Record not found</MissingRecordMessage>
        </CenteredBlock>
      );
    }
    if (data?.record === undefined) {
      return (
        <CenteredBlock>
          <LoadingSpinner loading={true} size="2x" />
        </CenteredBlock>
      );
    }
  }

  return (
    <div>
      <OpsInputForm
        onSubmit={formHandler.handleSubmit((newValues) =>
          onFormSubmit(newValues, data?.record, recordId)
        )}
      >
        <OpsInputFormSubTitle>Please enter Lost Unit Data</OpsInputFormSubTitle>

        <OpsInputFormInputSection>
          {formError() ? (
            <tbody>
              <tr>
                {isSuccess(formError()) ? (
                  <FormSuccessMessage colSpan={3}>
                    {formError()}
                  </FormSuccessMessage>
                ) : (
                  <FormErrorMessage colSpan={3}>{formError()}</FormErrorMessage>
                )}
              </tr>
            </tbody>
          ) : null}

          <OpsInputFormInputUpper
            label="DSKU *"
            maxLength={DSKULength}
            name={LostUnitsField.DSKU}
            placeholder="Ex. D13ZZ56ABC1"
            ref={refs[LostUnitsField.DSKU]}
            errors={errors}
          />

          <TableInputGap />

          <OpsInputFormInput
            label="Lost Quantity *"
            name={LostUnitsField.LOST_QUANTITY}
            numbersOnly
            placeholder="Ex. 5"
            ref={refs[LostUnitsField.LOST_QUANTITY]}
            errors={errors}
          />

          <OpsInputFormInput
            label="Found Quantity"
            name={LostUnitsField.FOUND_QUANTITY}
            numbersOnly
            placeholder="Ex. 5"
            ref={refs[LostUnitsField.FOUND_QUANTITY]}
            errors={errors}
          />

          <OpsInputFormInput
            label="Reimbursed Amount"
            name={LostUnitsField.REIMBURSED_AMOUNT}
            currencyOnly
            placeholder="Ex. 5"
            ref={refs[LostUnitsField.REIMBURSED_AMOUNT]}
            errors={errors}
          />

          <TableInputGap />

          <OpsInputFormInputUpper
            label="Warehouse"
            maxLength={maxWarehouseLength}
            name={LostUnitsField.WAREHOUSE}
            placeholder="Ex. PBD_ALP"
            ref={refs[LostUnitsField.WAREHOUSE]}
            errors={errors}
          />

          <OpsInputFormInput
            label="Shipping Plan"
            name={LostUnitsField.SHIPPINGPLAN}
            numbersOnly
            placeholder="Ex. 123435"
            ref={refs[LostUnitsField.SHIPPINGPLAN]}
            errors={errors}
          />

          <OpsInputFormInput
            label="FC ASN #"
            maxLength={maxASNLength}
            name={LostUnitsField.FC_ASN}
            numbersOnly
            placeholder="Ex. 160776"
            ref={refs[LostUnitsField.FC_ASN]}
            errors={errors}
          />

          <OpsInputFormInput
            label="Crossdock ASN #"
            maxLength={maxASNLength}
            name={LostUnitsField.CROSSDOCK_ASN}
            numbersOnly
            placeholder="Ex. 160776"
            ref={refs[LostUnitsField.CROSSDOCK_ASN]}
            errors={errors}
          />

          <TableInputGap />

          <OpsInputFormInputLower
            label="Seller"
            name={LostUnitsField.SELLER}
            placeholder="Ex. boxwidgets"
            ref={refs[LostUnitsField.SELLER]}
            errors={errors}
          />

          <OpsInputFormInputLower
            label="Reporter"
            name={LostUnitsField.REPORTER}
            placeholder="Ex. mary"
            ref={refs[LostUnitsField.REPORTER]}
            errors={errors}
          />

          <TableInputGap />

          <OpsInputFormInputLower
            label="Asana URL"
            name={LostUnitsField.ASANA}
            placeholder="Ex. https://app.asana.com/0/1147658396884857/1199546009653140/f"
            ref={refs[LostUnitsField.ASANA]}
            errors={errors}
          />

          <OpsInputFormInput
            label="Zendesk Ticket"
            name={LostUnitsField.ZENDESK}
            placeholder="Ex. 13"
            numbersOnly
            ref={refs[LostUnitsField.ZENDESK]}
            errors={errors}
          />

          <TableComponent
            label="Notes"
            ref={refs[LostUnitsField.LOST_DATE]}
            errors={errors}
            name={LostUnitsField.NOTES}
          >
            <OpsInputFormTextArea
              name={LostUnitsField.NOTES}
              placeholder="Ex. I did this and then that"
              ref={refs[LostUnitsField.NOTES]}
            />
          </TableComponent>

          <TableInputGap />

          <TableComponent
            label="Reason"
            ref={refs[LostUnitsField.REASON]}
            errors={errors}
            name={LostUnitsField.REASON}
          >
            <OpsInputFormSelect
              name={LostUnitsField.REASON}
              ref={refs[LostUnitsField.REASON]}
            >
              <option value="LOST">LOST</option>
              <option value="DAMAGED">DAMAGED</option>
            </OpsInputFormSelect>
          </TableComponent>

          <TableComponent
            label="Distribution Method"
            ref={refs[LostUnitsField.DISTRIBUTION]}
            errors={errors}
            name={LostUnitsField.DISTRIBUTION}
          >
            <OpsInputFormSelect
              name={LostUnitsField.DISTRIBUTION}
              ref={refs[LostUnitsField.DISTRIBUTION]}
            >
              <option value="CROSSDOCK">CROSSDOCK</option>
              <option value="DIRECT">DIRECT</option>
            </OpsInputFormSelect>
          </TableComponent>

          <OpsInputFormInput
            label="Lost on"
            ref={refs[LostUnitsField.LOST_DATE]}
            errors={errors}
            name={LostUnitsField.LOST_DATE}
            placeholder="Ex. 2021-01-10"
          />

          <tbody>
            <tr>
              <td />
              <td>
                <button disabled={!formState.isValid}>
                  {recordId === undefined
                    ? "Record Lost Units"
                    : "Save Changes"}
                </button>
              </td>
              <td />
            </tr>
          </tbody>
        </OpsInputFormInputSection>
      </OpsInputForm>

      {recordId === undefined ? null : (
        <button onClick={() => onDelete(recordId)}>Delete Record</button>
      )}
    </div>
  );
};
